import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, OnDestroy, SimpleChanges
} from '@angular/core';
import { PizzaRound } from '../../../interfaces/cutting-pizza.interface';

interface Wedge {
  i: number;
  path: string;
  midAngle: number;
  liftX: number;
  liftY: number;
  pepp: { x: number; y: number }[];
  spokeX: number;
  spokeY: number;
}
interface Sparkle { x: number; y: number; r: number; delay: number; }

/**
 * Cutting Pizza play surface — a bright pizza scored into `den` equal
 * slices, drawn on the shell's single white play-panel (the order header
 * lives at the top of that same panel; this SVG owns only the pizza /
 * plate / cutter that sit on the board — area model, NOT a number line/bar). The
 * child drags the **pizza cutter** onto a slice to cut it onto the plate
 * (or taps a slice); the plate shows the running fraction `served/den`.
 * SERVE (from the shell) checks the built count against the answer.
 *
 * The pizza, cutter and plate are drawn as layered life-like SVG art
 * (Rule-8 fidelity): a fully-detailed base pizza (crust + char + melty
 * cheese + sauce mottle + pepperoni + herbs) sits *under* the wedge
 * `*ngFor`, a serrated steel-and-wood cutter, and a glazed ceramic plate
 * — all via deterministic gradients/highlights/shadows, no per-frame
 * randomness and no filters on the moving cutter group.
 *
 * The base pizza always stays WHOLE. Selecting a slice MARKS it in place
 * ("highlighted in place"): the slice stays flat in the pizza with a
 * deeper/warmer tint (`cp-mark`), a warm outer glow + a dark-outlined
 * bold deep-amber border (double-stroke, high-contrast on golden pizza) —
 * no lift, no carved gap (the pizza's own
 * toppings still show through the translucent tint). The same served slices are
 * re-plated on the dish as a clipped real mini-pizza (cheese + accumulating
 * sauce/pepperoni/herb toppings, kept inside the served pie by
 * `cp-plate-slice`).
 *
 * Presentation-only, like the other skill stages: it owns the cutter
 * drag and reports via `solved` / `missed`.
 * Scoring / turns / retries live in the shell + service. A wrong serve
 * is never the end — the board wobbles and the plate resets so the child
 * re-reasons; the answer count is never revealed.
 */
@Component({
  selector: 'app-cutting-pizza-stage',
  templateUrl: './cutting-pizza-stage.component.html',
  styleUrls: ['./cutting-pizza-stage.component.scss']
})
export class CuttingPizzaStageComponent implements OnChanges, OnDestroy {
  @Input() round: PizzaRound | null = null;
  @Input() roundKey = 0;
  @Input() hintVisible = false;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 640;
  readonly CX = 355;          // pizza centre
  readonly CY = 335;
  readonly R = 210;           // pizza radius
  readonly LIFT = 14;         // how far a served slice pops out
  readonly PX = 800;          // plate centre
  readonly PY = 300;
  readonly PR = 92;           // plate mini-pizza radius
  readonly PARK_X = 815;      // cutter resting spot
  readonly PARK_Y = 545;

  served: boolean[] = [];
  cutterX = 815;
  cutterY = 545;
  hoverWedge: number | null = null;
  solvedState = false;
  bumped = false;
  wrongFlash = false;
  sparkles: Sparkle[] = [];

  private dragging = false;
  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  /** Clear any pending wrong-serve timers so callbacks never fire on a dead view. */
  ngOnDestroy(): void {
    this.clearTimers();
  }

  private reset(): void {
    this.clearTimers();
    this.solvedState = false;
    this.bumped = false;
    this.wrongFlash = false;
    this.sparkles = [];
    this.dragging = false;
    this.hoverWedge = null;
    this.cutterX = this.PARK_X;
    this.cutterY = this.PARK_Y;
    this.served = new Array(this.den).fill(false);
  }

  // ── Geometry ───────────────────────────────────────────────────────

  get den(): number { return this.round?.den ?? 4; }
  get step(): number { return (Math.PI * 2) / this.den; }
  get servedCount(): number { return this.served.filter(Boolean).length; }

  private polar(angle: number, r: number, cx = this.CX, cy = this.CY): { x: number; y: number } {
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  }

  /** Every slice: its wedge path, lift vector, pepperoni + spoke line. */
  get wedges(): Wedge[] {
    const out: Wedge[] = [];
    for (let i = 0; i < this.den; i++) {
      const t0 = -Math.PI / 2 + i * this.step;
      const t1 = t0 + this.step;
      const mid = t0 + this.step / 2;
      const p0 = this.polar(t0, this.R);
      const p1 = this.polar(t1, this.R);
      const spoke = this.polar(t0, this.R);
      out.push({
        i,
        path: `M ${this.CX} ${this.CY} L ${p0.x.toFixed(1)} ${p0.y.toFixed(1)} ` +
              `A ${this.R} ${this.R} 0 0 1 ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} Z`,
        midAngle: mid,
        liftX: Math.cos(mid) * this.LIFT,
        liftY: Math.sin(mid) * this.LIFT,
        pepp: [this.polar(mid, this.R * 0.55), this.polar(mid, this.R * 0.82)],
        spokeX: spoke.x,
        spokeY: spoke.y
      });
    }
    return out;
  }

  get cutterTransform(): string { return `translate(${this.cutterX.toFixed(1)}, ${this.cutterY.toFixed(1)})`; }

  // ── Static life-like pizza detail (Rule-8 art direction) ────────────
  // All deterministic — no per-frame randomness — so it renders under the
  // wedge *ngFor as the always-present, fully-detailed base pizza.

  private readonly PEPP_BASE = [
    { a: -108, rf: 0.32, r: 17 }, { a: -40, rf: 0.60, r: 16 },
    { a:   28, rf: 0.38, r: 18 }, { a:  82, rf: 0.66, r: 16 },
    { a:  150, rf: 0.44, r: 17 }, { a: -150, rf: 0.66, r: 16 },
    { a:  175, rf: 0.30, r: 15 }, { a: -70, rf: 0.80, r: 14 },
  ];
  get basePepp() {
    return this.PEPP_BASE.map(p => {
      const rad = (p.a * Math.PI) / 180;
      return { x: this.CX + this.R * p.rf * Math.cos(rad),
               y: this.CY + this.R * p.rf * Math.sin(rad), r: p.r };
    });
  }

  private readonly HERB_BASE = [
    { a: -80, rf: 0.5 }, { a: -20, rf: 0.72 }, { a: 55, rf: 0.5 },
    { a: 110, rf: 0.34 }, { a: 130, rf: 0.7 }, { a: -130, rf: 0.4 },
    { a: 165, rf: 0.55 }, { a: 8, rf: 0.62 }, { a: -55, rf: 0.28 },
    { a: 95, rf: 0.82 },
  ];
  get baseHerbs() {
    return this.HERB_BASE.map((h, i) => {
      const rad = (h.a * Math.PI) / 180;
      return { x: this.CX + this.R * h.rf * Math.cos(rad),
               y: this.CY + this.R * h.rf * Math.sin(rad),
               rot: (h.a + i * 37) % 180 };
    });
  }

  readonly CHAR_ANGLES = [-70, -15, 40, 100, 155, -120, 200];
  get crustChars() {
    return this.CHAR_ANGLES.map(a => {
      const rad = (a * Math.PI) / 180;
      return { x: this.CX + (this.R - 6) * Math.cos(rad),
               y: this.CY + (this.R - 6) * Math.sin(rad), rot: a };
    });
  }

  get cheeseEdge(): string {
    const bumps = 18, rO = this.R - 14, rI = this.R - 22;
    let d = '';
    for (let k = 0; k <= bumps * 2; k++) {
      const a = -Math.PI / 2 + (Math.PI * k) / bumps;
      const r = k % 2 === 0 ? rO : rI;
      d += (k === 0 ? 'M' : 'L') +
           (this.CX + r * Math.cos(a)).toFixed(1) + ' ' +
           (this.CY + r * Math.sin(a)).toFixed(1) + ' ';
    }
    return d + 'Z';
  }

  /** 3 translucent sauce mottle blots (deterministic). */
  private readonly SAUCE_BASE = [
    { a: 20, rf: 0.45 }, { a: -95, rf: 0.45 }, { a: 150, rf: 0.45 },
  ];
  get sauceMottle() {
    return this.SAUCE_BASE.map(s => {
      const rad = (s.a * Math.PI) / 180;
      return { x: this.CX + this.R * s.rf * Math.cos(rad),
               y: this.CY + this.R * s.rf * Math.sin(rad) };
    });
  }

  /** 4 melted cheese bubbles at fixed spots. */
  get cheeseBubbles() {
    return [
      { x: this.CX - 40, y: this.CY - 60 },
      { x: this.CX + 55, y: this.CY - 20 },
      { x: this.CX - 10, y: this.CY + 70 },
      { x: this.CX + 30, y: this.CY + 40 },
    ];
  }

  /** Serrated star-path for the cutter wheel rim (local coords, centred 0,2). */
  readonly wheelTeeth: string = (() => {
    const cx = 0, cy = 2, teeth = 24, rOut = 32, rIn = 28;
    let d = '';
    for (let k = 0; k < teeth * 2; k++) {
      const a = (Math.PI * k) / teeth;
      const r = k % 2 === 0 ? rOut : rIn;
      d += (k === 0 ? 'M' : 'L') +
           (cx + r * Math.cos(a)).toFixed(1) + ' ' +
           (cy + r * Math.sin(a)).toFixed(1) + ' ';
    }
    return d + 'Z';
  })();

  /** Built fraction on the plate as a filled pie (served/den). */
  get platePie(): string {
    const n = this.servedCount;
    if (n <= 0) return '';
    if (n >= this.den) {
      return `M ${this.PX} ${this.PY - this.PR} A ${this.PR} ${this.PR} 0 1 1 ${(this.PX - 0.01).toFixed(2)} ${this.PY - this.PR} Z`;
    }
    const t0 = -Math.PI / 2;
    const t1 = t0 + this.step * n;
    const p0 = this.polar(t0, this.PR, this.PX, this.PY);
    const p1 = this.polar(t1, this.PR, this.PX, this.PY);
    const large = this.step * n > Math.PI ? 1 : 0;
    return `M ${this.PX} ${this.PY} L ${p0.x.toFixed(1)} ${p0.y.toFixed(1)} ` +
           `A ${this.PR} ${this.PR} 0 ${large} 1 ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} Z`;
  }

  /** Just the crust arc of the plate pie (browned outer rim, no straight cut edges). */
  get platePieArc(): string {
    const n = this.servedCount;
    if (n <= 0) return '';
    const t0 = -Math.PI / 2;
    if (n >= this.den) {
      return `M ${this.PX} ${this.PY - this.PR} A ${this.PR} ${this.PR} 0 1 1 ${(this.PX - 0.01).toFixed(2)} ${this.PY - this.PR}`;
    }
    const p0 = this.polar(t0, this.PR, this.PX, this.PY);
    const p1 = this.polar(t0 + this.step * n, this.PR, this.PX, this.PY);
    const large = this.step * n > Math.PI ? 1 : 0;
    return `M ${p0.x.toFixed(1)} ${p0.y.toFixed(1)} ` +
           `A ${this.PR} ${this.PR} 0 ${large} 1 ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }

  /** Mid-angle of the k-th contiguous plate slice. */
  private midK(k: number): number { return -Math.PI / 2 + this.step * (k + 0.5); }
  private plateSlots(): number[] { return Array.from({ length: this.servedCount }, (_, k) => k); }

  /** One pepperoni per served slice, so toppings accumulate as the plate fills. */
  get platePepp() {
    const r = Math.min(11, this.PR * 0.13);
    return this.plateSlots().map(k => {
      const a = this.midK(k);
      return { x: this.PX + this.PR * 0.58 * Math.cos(a), y: this.PY + this.PR * 0.58 * Math.sin(a), r };
    });
  }
  /** One sauce blot per served slice. */
  get plateSauce() {
    return this.plateSlots().map(k => {
      const a = this.midK(k) + 0.25;
      return { x: this.PX + this.PR * 0.42 * Math.cos(a), y: this.PY + this.PR * 0.42 * Math.sin(a) };
    });
  }
  /** One herb fleck per served slice. */
  get plateHerbs() {
    return this.plateSlots().map(k => {
      const a = this.midK(k) - 0.30;
      return { x: this.PX + this.PR * 0.30 * Math.cos(a), y: this.PY + this.PR * 0.30 * Math.sin(a), rot: (k * 57) % 180 };
    });
  }

  // ── Cut with the cutter (drag) or tap a slice ──────────────────────

  private wedgeAt(px: number, py: number): number | null {
    const dx = px - this.CX;
    const dy = py - this.CY;
    const dist = Math.hypot(dx, dy);
    if (dist > this.R + 30) return null;
    let rel = Math.atan2(dy, dx) + Math.PI / 2;   // measured clockwise from top
    rel = ((rel % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return Math.min(this.den - 1, Math.floor(rel / this.step));
  }

  onCutterDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.dragging = true;
    this.bumped = false;
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onPointerMove(evt: PointerEvent): void {
    if (!this.dragging) return;
    const p = this.toSvg(evt);
    this.cutterX = p.x;
    this.cutterY = p.y;
    this.hoverWedge = this.wedgeAt(p.x, p.y);
  }

  onPointerUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
    if (this.hoverWedge != null) this.toggle(this.hoverWedge);
    this.hoverWedge = null;
    // Snap the cutter back to its parking spot.
    this.cutterX = this.PARK_X;
    this.cutterY = this.PARK_Y;
  }

  /** Tapping a slice directly toggles it too (touch-screen friendly). */
  onWedgeTap(i: number): void {
    if (!this.interactive || this.solvedState || this.dragging) return;
    this.toggle(i);
  }

  private toggle(i: number): void {
    this.served = this.served.map((v, idx) => (idx === i ? !v : v));
  }

  get canServe(): boolean {
    return this.interactive && !this.solvedState;
  }

  // ── Serve (called by the shell's SERVE button) ─────────────────────

  serve(): void {
    if (!this.canServe || !this.round) return;
    if (this.servedCount === this.round.answerNum) {
      this.solvedState = true;
      this.spawnSparkles();
      this.solved.emit();
    } else {
      this.bumped = true;
      this.wrongFlash = true;
      this.missed.emit();
      this.timers.push(setTimeout(() => { this.wrongFlash = false; }, 600));
      this.timers.push(setTimeout(() => {
        this.served = new Array(this.den).fill(false);
        this.bumped = false;
      }, 640));
    }
  }

  private spawnSparkles(): void {
    this.sparkles = Array.from({ length: 14 }, (_, i) => {
      const a = (i / 14) * Math.PI * 2;
      const dist = 30 + (i % 3) * 20;
      return { x: this.CX + Math.cos(a) * dist, y: this.CY + Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
  }

  private toSvg(evt: PointerEvent): { x: number; y: number } {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = pt.matrixTransform(ctm.inverse());
    return { x: m.x, y: m.y };
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
